import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ResultsFileUploadDialog } from '@/features/dashboard/sections/ResultsFileUploadDialog';
import { PageLoadingFallback } from '@/features/metrics/components/PageLoadingFallback';
import { DelayedSkeletonGate } from '@/ui/composites';
import { mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Button } from '@/ui/primitives';
import { EntityPageContent } from './EntityPageContent';
import type {
  EntityStatusKey,
  EntityTrendsPageViewModel,
} from '@/features/shared/hooks/useEntityTrendsPage';
import type { GridFilters } from '@/ui/composites/grid/gridFilters';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult } from '@flowqa/shared';
import type { ReactElement, ReactNode } from 'react';

type EntityTrendsPageShellProps<TEntityItem> = {
  viewModel: EntityTrendsPageViewModel<TEntityItem>;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  chartEmptyStateTitle: string;
  entityTableNoRowsText: string;
  executionTableNoRowsText: string;
  persistKey: string;
  executionPersistKey: string;
  renderEntityTable: (input: {
    queryKey: readonly unknown[];
    queryFn: (queryInput: GridQueryInput) => Promise<QueryResult<TEntityItem>>;
    fixedFilters: GridFilters;
    search: string;
    showDeltas: boolean;
    persistKey: string;
    noRowsText: string;
  }) => ReactElement;
  renderExecutionsTable: (input: {
    fixedFilters: GridFilters;
    search: string;
    persistKey: string;
    noRowsText: string;
  }) => ReactElement;
  contentBelow?: ReactNode;
  contentOverride?: ReactNode;
};

export const EntityTrendsPageShell = <TEntityItem,>({
  viewModel,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  chartEmptyStateTitle,
  entityTableNoRowsText,
  executionTableNoRowsText,
  persistKey,
  executionPersistKey,
  renderEntityTable,
  renderExecutionsTable,
  contentBelow,
  contentOverride,
}: EntityTrendsPageShellProps<TEntityItem>): ReactElement => {
  const loadingFallback = <PageLoadingFallback message={viewModel.loadingMessage} />;
  const titleActions = (
    <Button onClick={viewModel.uploadFlow.openUploadDialog}>Upload Results Files</Button>
  );
  const uploadDialogNode = (
    <ResultsFileUploadDialog
      open={viewModel.uploadFlow.uploadOpen}
      loading={viewModel.uploadFlow.uploadMutation.isPending}
      confirmDisabled={viewModel.uploadFlow.disableUpload}
      onClose={viewModel.uploadFlow.closeUploadDialog}
      onConfirm={() => {
        const projectId = viewModel.selectedProjectId ?? viewModel.uploadFlow.uploadProjectId;
        if (!projectId) {
          viewModel.uploadFlow.setUploadError('Select a project for this upload.');
          return;
        }
        if (viewModel.uploadFlow.selectedUploadFiles.length === 0) {
          viewModel.uploadFlow.setUploadError('Select at least one supported file to upload.');
          return;
        }
        viewModel.uploadFlow.setUploadError(null);
        const filesToUpload = viewModel.uploadFlow.selectedUploadFiles;
        const tagsToUpload = viewModel.uploadFlow.uploadTagValues;
        viewModel.uploadFlow.closeUploadDialog();
        viewModel.uploadFlow.startUpload(projectId, filesToUpload, tagsToUpload);
      }}
      selectedProjectId={viewModel.selectedProjectId ?? null}
      uploadProjectId={viewModel.uploadFlow.uploadProjectId}
      uploadTagsInput={viewModel.uploadFlow.uploadTagsInput}
      onUploadProjectIdChange={viewModel.uploadFlow.setUploadProjectId}
      onUploadTagsInputChange={viewModel.uploadFlow.setUploadTagsInput}
      uploadProjectIdFieldId={viewModel.uploadProjectIdFieldId}
      projectOptions={viewModel.projectOptions}
      uploadFiles={viewModel.uploadFlow.uploadFiles}
      selectedFiles={viewModel.uploadFlow.selectedFiles}
      isSupportedFile={viewModel.uploadFlow.isSupportedFile}
      csvHeaderStatusByName={viewModel.uploadFlow.csvHeaderStatusByName}
      csvHeaderReasonByName={viewModel.uploadFlow.csvHeaderReasonByName}
      fileValidationStatusByName={viewModel.uploadFlow.fileValidationStatusByName}
      fileValidationReasonByName={viewModel.uploadFlow.fileValidationReasonByName}
      handleFilesSelected={viewModel.uploadFlow.handleFilesSelected}
      setSelectedFiles={viewModel.uploadFlow.setSelectedFiles}
      setUploadFiles={viewModel.uploadFlow.setUploadFiles}
      setUploadError={viewModel.uploadFlow.setUploadError}
      uploadError={viewModel.uploadFlow.uploadError}
      setCsvHeaderStatusByName={viewModel.uploadFlow.setCsvHeaderStatusByName}
      setCsvHeaderReasonByName={viewModel.uploadFlow.setCsvHeaderReasonByName}
      setFileValidationStatusByName={viewModel.uploadFlow.setFileValidationStatusByName}
      setFileValidationReasonByName={viewModel.uploadFlow.setFileValidationReasonByName}
    />
  );
  const resolvedChartEmptyStateTitle = formatRangeWindowNoDataText(
    chartEmptyStateTitle,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedEntityTableNoRowsText = formatRangeWindowNoDataText(
    entityTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedExecutionTableNoRowsText = formatRangeWindowNoDataText(
    executionTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );

  return (
    <DelayedSkeletonGate show={viewModel.showPageSkeleton} fallback={loadingFallback}>
      <EntityPageContent
        titleActions={titleActions}
        footer={uploadDialogNode}
        range={viewModel.range}
        chartTab={viewModel.chartTab}
        activeTab={viewModel.activeTab}
        entityTabValue="entity"
        searchValue={viewModel.searchValue}
        pickerStartDay={viewModel.pickerStartDay}
        pickerEndDay={viewModel.pickerEndDay}
        hasCustomDates={viewModel.hasCustomDates}
        isLatestMode={viewModel.isLatestMode}
        metricCards={viewModel.metricCards}
        selectedStatuses={viewModel.selectedStatuses}
        statusOptions={viewModel.statusFilterOptions}
        selectedTags={viewModel.selectedTags}
        tagOptions={viewModel.tagOptions}
        tagOptionsLoading={viewModel.tagOptionsLoading}
        metricValueItems={viewModel.metricValueItems}
        chartHeight={viewModel.chartHeight}
        showMetricCardsSkeleton={viewModel.showMetricCardsSkeleton}
        showChartSkeleton={viewModel.showChartSkeleton}
        suppressChartAnimation={viewModel.suppressChartAnimation}
        showLineChartPoints={viewModel.showLineChartPoints}
        filteredStackedChartData={viewModel.filteredStackedChartData}
        filteredPassRateChartData={viewModel.filteredPassRateChartData}
        filteredFrequencyChartData={viewModel.filteredFrequencyChartData}
        filteredDurationChartData={viewModel.filteredDurationChartData}
        deltasEnabled={viewModel.deltasEnabled}
        onRangeChange={viewModel.handleRangeChange}
        onSearchChange={viewModel.handleSearchChange}
        onChartTabChange={viewModel.handleChartTabChange}
        onActiveTabChange={viewModel.handleActiveTabChange}
        onStatusToggle={(status) => {
          viewModel.handleStatusToggle(status as EntityStatusKey);
        }}
        onStatusesChange={(nextStatuses) => {
          viewModel.handleStatusesChange(nextStatuses as EntityStatusKey[]);
        }}
        onTagsChange={viewModel.handleTagsChange}
        onChartSelect={viewModel.handleChartSelect}
        onSelectedStartDayChange={viewModel.handleSelectedStartDayChange}
        onSelectedEndDayChange={viewModel.handleSelectedEndDayChange}
        showChartDateClear={viewModel.showChartDateClear}
        onChartDateClear={viewModel.handleChartDateClear}
        title={title}
        addButtonLabel={addButtonLabel}
        searchAriaLabel={searchAriaLabel}
        chartTabsAriaLabel={chartTabsAriaLabel}
        pageTabsAriaLabel={pageTabsAriaLabel}
        entityTabLabel={entityTabLabel}
        chartEmptyStateTitle={resolvedChartEmptyStateTitle}
        entityTableNoRowsText={resolvedEntityTableNoRowsText}
        executionTableNoRowsText={resolvedExecutionTableNoRowsText}
        contentBelow={contentBelow}
        contentOverride={contentOverride}
        renderEntityTable={(showTableDeltas) =>
          renderEntityTable({
            queryKey: viewModel.queryKey,
            queryFn: viewModel.queryFn,
            fixedFilters: viewModel.entityTableFilters,
            search: viewModel.debouncedSearchValue,
            showDeltas: showTableDeltas,
            persistKey,
            noRowsText: resolvedEntityTableNoRowsText,
          })
        }
        renderExecutionsTable={() =>
          renderExecutionsTable({
            fixedFilters: mergeFilters(
              viewModel.executionFilters,
              viewModel.executionStatusFilters,
            ),
            search: viewModel.debouncedSearchValue,
            persistKey: executionPersistKey,
            noRowsText: resolvedExecutionTableNoRowsText,
          })
        }
      />
    </DelayedSkeletonGate>
  );
};
